import i18n from "i18n";
import { useQuery } from "@tanstack/react-query";
import { ArchiveClusterArchives, ArchiveConfig } from "@graylog/enterprise-api";
import { LICENSE_SUBJECTS } from "license/constants";
import useLicenseValidityForSubject from "license/hooks/useLicenseValidityForSubject";
import { defaultOnError } from "util/conditional/onError";
import { ARCHIVE_CREATE_SYSTEM_JOB_NAME } from "./constants";
const ARCHIVE_JOB_ALREADY_RUNNING_MESSAGE = /archive.*job.*is already running/i;
const CATALOG_LOOKUP_MAX_ENTRIES = 1e4;
const EMPTY_SET = /* @__PURE__ */ new Set();
const useCanArchive = () => {
  const { data: license } = useLicenseValidityForSubject(LICENSE_SUBJECTS.archive);
  const { data: config } = useQuery({
    queryKey: ["archive-config"],
    queryFn: () => defaultOnError(
      ArchiveConfig.get(),
      i18n.t("notifications.fetch_archive_config_failed", { ns: "enterprise_archive" }),
      i18n.t("notifications.fetch_archive_config_failed_title", { ns: "enterprise_archive" })
    ),
    enabled: license.valid,
    retry: false
  });
  return license.valid && !!(config == null ? void 0 : config.backend_id);
};
const buildIndexNameQuery = (indexNames) => indexNames.map((name) => `index:=${name}`).join(" ");
const fetchArchivedIndexNames = async (indexNames) => {
  var _a;
  const page = await ArchiveClusterArchives.catalog(
    "index_name",
    1,
    CATALOG_LOOKUP_MAX_ENTRIES,
    "asc",
    buildIndexNameQuery(indexNames)
  );
  return new Set(((_a = page.archives) != null ? _a : []).map((entry) => entry.index_name).filter((name) => !!name));
};
const useArchivedIndexNames = (indexNames, enabled) => {
  const sortedIndexNames = [...indexNames].sort();
  const isEnabled = enabled && sortedIndexNames.length > 0;
  const { data } = useQuery({
    queryKey: ["archived-index-names", sortedIndexNames],
    queryFn: () => defaultOnError(
      fetchArchivedIndexNames(sortedIndexNames),
      i18n.t("notifications.fetch_archived_indices_failed", { ns: "enterprise_archive" }),
      i18n.t("notifications.fetch_archived_indices_failed_title", { ns: "enterprise_archive" })
    ),
    enabled: isEnabled
  });
  return data != null ? data : EMPTY_SET;
};
const archiveAndDeleteIndex = async (indexName) => {
  var _a;
  const response = await ArchiveClusterArchives.run(indexName, "DELETE");
  const systemJobId = (_a = response == null ? void 0 : response.system_job) == null ? void 0 : _a.id;
  if (!systemJobId) {
    throw new Error("Archive job did not return a job id.");
  }
  return { systemJobId };
};
const archiveAndDeleteIndices = async (indexNames) => {
  var _a;
  const response = await ArchiveClusterArchives.bulkArchiveIndices({
    indices: indexNames,
    index_action: "DELETE"
  });
  const systemJobId = (_a = response == null ? void 0 : response.system_job) == null ? void 0 : _a.id;
  if (!systemJobId) {
    throw new Error("Archive job did not return a job id.");
  }
  return { systemJobId };
};
const indexArchiveBinding = {
  useCanArchive,
  useArchivedIndexNames,
  archiveAndDeleteIndex,
  archiveAndDeleteIndices,
  isArchiveJobConflict: (errorMessage) => ARCHIVE_JOB_ALREADY_RUNNING_MESSAGE.test(errorMessage),
  archiveSystemJobName: ARCHIVE_CREATE_SYSTEM_JOB_NAME
};
export default indexArchiveBinding;
